import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Popover, Stack, Text, Slider, Select, Button, Switch, ScrollArea, Tabs } from '@mantine/core';
import type { PortalProps } from '@mantine/core';
import { IconSun } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import * as modelViewer from '@google/model-viewer';
import {
  DEFAULT_MODEL_VIEWER_PRESENTATION,
  MODEL_VIEWER_BACKGROUND_I18N_KEYS,
  MODEL_VIEWER_BACKGROUND_OPTIONS,
  MODEL_VIEWER_ENVIRONMENT_OPTIONS,
  MODEL_VIEWER_ROTATION_SPEED_OPTIONS,
  MODEL_VIEWER_SKYBOX_OPTIONS,
  MODEL_VIEWER_TONE_MAPPING_OPTIONS,
  type ModelViewerPresentationSettings,
} from '@/constants/modelViewerPresentation';
import { hexToRgba } from '@/utils/colorUtils';
import { useBrandForeground } from '@/utils/brandForeground';
import {
  VIEWER_CONTROL_CHROME_HOVER_BG,
  VIEWER_CONTROL_CHROME_STYLE,
} from './viewerControlChrome';

type PresentationTab = 'lighting' | 'sky' | 'motion' | 'materials';

interface ModelViewerPresentationPanelProps {
  settings: ModelViewerPresentationSettings;
  onChange: (settings: ModelViewerPresentationSettings) => void;
  modelViewerRef?: React.RefObject<modelViewer.ModelViewerElement | null>;
  color?: string;
  inline?: boolean;
  bottomSpacing?: string;
  menuButtonRight?: string;
  immersiveLayerBaseZ?: number;
  menuPortalProps?: Omit<PortalProps, 'children'>;
}

function floatingViewerButtonStyle(
  color: string,
  inline: boolean,
  bottomSpacing: string | undefined,
  menuButtonRight: string | undefined,
  zIndex: number
): React.CSSProperties {
  return {
    ...VIEWER_CONTROL_CHROME_STYLE,
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    padding: 0,
    color,
    fontSize: '0.875rem',
    fontWeight: 500,
    transform: 'scale(1)',
    ...(inline
      ? { position: 'relative', zIndex }
      : {
          position: 'absolute',
          bottom: bottomSpacing ?? '16px',
          right: menuButtonRight ?? '16px',
          zIndex,
        }),
  };
}

export function ModelViewerPresentationPanel({
  settings,
  onChange,
  modelViewerRef,
  color = '#5D7723',
  inline = false,
  bottomSpacing,
  menuButtonRight,
  immersiveLayerBaseZ,
  menuPortalProps,
}: ModelViewerPresentationPanelProps) {
  const iconColor = useBrandForeground(color);
  const { t } = useTranslation();
  const popoverZ = immersiveLayerBaseZ != null ? immersiveLayerBaseZ + 600 : 1001;
  const buttonZ = immersiveLayerBaseZ != null ? immersiveLayerBaseZ + 45 : 12;
  const [availableVariants, setAvailableVariants] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<PresentationTab>('lighting');

  const hasMaterialsTab = availableVariants.length > 0;

  const patch = useCallback(
    (partial: Partial<ModelViewerPresentationSettings>) => {
      onChange({ ...settings, ...partial });
    },
    [onChange, settings]
  );

  useEffect(() => {
    const el = modelViewerRef?.current;
    if (!el) {
      setAvailableVariants([]);
      return;
    }

    const syncVariants = () => {
      try {
        const variants = el.availableVariants ?? [];
        setAvailableVariants(variants.filter((name) => name && name.trim() !== ''));
      } catch {
        setAvailableVariants([]);
      }
    };

    const handleLoad = () => setTimeout(syncVariants, 100);
    el.addEventListener('load', handleLoad);
    syncVariants();

    const interval = setInterval(syncVariants, 800);
    return () => {
      el.removeEventListener('load', handleLoad);
      clearInterval(interval);
    };
  }, [modelViewerRef, modelViewerRef?.current]);

  useEffect(() => {
    if (activeTab === 'materials' && !hasMaterialsTab) {
      setActiveTab('lighting');
    }
  }, [activeTab, hasMaterialsTab]);

  const toneOptions = MODEL_VIEWER_TONE_MAPPING_OPTIONS.map((value) => ({
    value,
    label: t(`modelViewer.presentation.toneMapping.${value}`),
  }));

  const environmentOptions = MODEL_VIEWER_ENVIRONMENT_OPTIONS.map((value) => ({
    value,
    label: t(`modelViewer.presentation.environment.${value}`),
  }));

  const skyboxOptions = MODEL_VIEWER_SKYBOX_OPTIONS.map((value) => ({
    value,
    label: t(`modelViewer.presentation.skybox.${value}`),
  }));

  const rotationOptions = MODEL_VIEWER_ROTATION_SPEED_OPTIONS.map((value) => ({
    value,
    label: t(`modelViewer.presentation.rotationSpeed.${value}`),
  }));

  const backgroundOptions = MODEL_VIEWER_BACKGROUND_OPTIONS.map((value) => ({
    value,
    label: t(`modelViewer.presentation.background.${MODEL_VIEWER_BACKGROUND_I18N_KEYS[value]}`),
  }));

  const variantOptions = useMemo(
    () => [
      { value: '', label: t('modelViewer.presentation.variantDefault') },
      ...availableVariants.map((name) => ({ value: name, label: name })),
    ],
    [availableVariants, t]
  );

  const sliderStyles = useMemo(
    () => ({
      bar: { backgroundColor: color },
      thumb: {
        borderColor: color,
        backgroundColor: '#fff',
        boxShadow: `0 0 0 2px ${hexToRgba(color, 0.25)}`,
      },
      track: { backgroundColor: hexToRgba(color, 0.2) },
      label: { backgroundColor: color, color: '#fff' },
    }),
    [color]
  );

  const selectStyles = useMemo(
    () => ({
      input: {
        '&:focus, &:focus-within': { borderColor: color },
      },
      option: {
        '&[data-combobox-selected]': {
          backgroundColor: hexToRgba(color, 0.15),
          color,
        },
        '&[data-combobox-active]': {
          backgroundColor: hexToRgba(color, 0.08),
        },
      },
    }),
    [color]
  );

  const resetButtonStyles = useMemo(
    () => ({
      root: {
        backgroundColor: hexToRgba(color, 0.12),
        color,
        '&:hover': { backgroundColor: hexToRgba(color, 0.2) },
      },
    }),
    [color]
  );

  const switchStyles = useMemo(
    () => ({
      track: {
        '&[data-checked]': { backgroundColor: color, borderColor: color },
      },
    }),
    [color]
  );

  const tabsStyles = useMemo(
    () => ({
      tab: {
        fontSize: '11px',
        padding: '6px 8px',
        '&[data-active]': {
          color,
          borderColor: color,
        },
      },
    }),
    [color]
  );

  const selectComboboxProps = useMemo(
    () => ({
      withinPortal: false as const,
      zIndex: popoverZ + 10,
    }),
    [popoverZ]
  );

  const tabPanel = (children: React.ReactNode) => (
    <ScrollArea.Autosize mah={280} type="auto" offsetScrollbars>
      <Stack gap="sm" pt="xs" pb={4}>
        {children}
      </Stack>
    </ScrollArea.Autosize>
  );

  return (
    <Popover
      width={320}
      position="top-end"
      shadow="md"
      zIndex={popoverZ}
      withinPortal
      portalProps={menuPortalProps}
      trapFocus={false}
      closeOnClickOutside
      styles={{ dropdown: { overflow: 'visible', padding: 0 } }}
    >
      <Popover.Target>
        <button
          type="button"
          style={floatingViewerButtonStyle(
            iconColor,
            inline,
            bottomSpacing,
            menuButtonRight,
            buttonZ
          )}
          title={t('modelViewer.presentation.title')}
          aria-label={t('modelViewer.presentation.title')}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.backgroundColor = VIEWER_CONTROL_CHROME_HOVER_BG;
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.backgroundColor = VIEWER_CONTROL_CHROME_STYLE.backgroundColor;
            e.currentTarget.style.boxShadow = VIEWER_CONTROL_CHROME_STYLE.boxShadow;
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = 'scale(0.95)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          className="desktop-ar-buttons"
        >
          <IconSun size={20} />
        </button>
      </Popover.Target>

      <Popover.Dropdown>
        <Stack gap="xs" p="sm">
          <Text size="sm" fw={600} style={{ color }}>
            {t('modelViewer.presentation.title')}
          </Text>

          <Tabs
            value={activeTab}
            onChange={(value) => {
              if (value) setActiveTab(value as PresentationTab);
            }}
            styles={tabsStyles}
          >
            <Tabs.List grow>
              <Tabs.Tab value="lighting">{t('modelViewer.presentation.tabLighting')}</Tabs.Tab>
              <Tabs.Tab value="sky">{t('modelViewer.presentation.tabSky')}</Tabs.Tab>
              <Tabs.Tab value="motion">{t('modelViewer.presentation.tabMotion')}</Tabs.Tab>
              {hasMaterialsTab && (
                <Tabs.Tab value="materials">{t('modelViewer.presentation.tabMaterials')}</Tabs.Tab>
              )}
            </Tabs.List>

            <Tabs.Panel value="lighting">
              {tabPanel(
                <>
                  <div>
                    <Text size="xs" mb={4}>
                      {t('modelViewer.presentation.shadowIntensity')}
                    </Text>
                    <Slider
                      min={0}
                      max={2}
                      step={0.05}
                      value={settings.shadowIntensity}
                      onChange={(value) => patch({ shadowIntensity: value })}
                      label={(value) => value.toFixed(2)}
                      styles={sliderStyles}
                    />
                  </div>

                  <div>
                    <Text size="xs" mb={4}>
                      {t('modelViewer.presentation.shadowSoftness')}
                    </Text>
                    <Slider
                      min={0}
                      max={1}
                      step={0.05}
                      value={settings.shadowSoftness}
                      onChange={(value) => patch({ shadowSoftness: value })}
                      label={(value) => value.toFixed(2)}
                      styles={sliderStyles}
                    />
                  </div>

                  <div>
                    <Text size="xs" mb={4}>
                      {t('modelViewer.presentation.exposure')}
                    </Text>
                    <Slider
                      min={0}
                      max={2}
                      step={0.05}
                      value={settings.exposure}
                      onChange={(value) => patch({ exposure: value })}
                      label={(value) => value.toFixed(2)}
                      styles={sliderStyles}
                    />
                  </div>

                  <Select
                    label={t('modelViewer.presentation.toneMappingLabel')}
                    data={toneOptions}
                    value={settings.toneMapping}
                    onChange={(value) => {
                      if (value) {
                        patch({ toneMapping: value as ModelViewerPresentationSettings['toneMapping'] });
                      }
                    }}
                    comboboxProps={selectComboboxProps}
                    styles={selectStyles}
                  />

                  <Select
                    label={t('modelViewer.presentation.environmentLabel')}
                    data={environmentOptions}
                    value={settings.environmentImage}
                    onChange={(value) => {
                      if (value) {
                        patch({
                          environmentImage: value as ModelViewerPresentationSettings['environmentImage'],
                        });
                      }
                    }}
                    comboboxProps={selectComboboxProps}
                    styles={selectStyles}
                  />
                </>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="sky">
              {tabPanel(
                <>
                  <Select
                    label={t('modelViewer.presentation.skyboxLabel')}
                    data={skyboxOptions}
                    value={settings.skyboxImage}
                    onChange={(value) => {
                      if (value) {
                        patch({ skyboxImage: value as ModelViewerPresentationSettings['skyboxImage'] });
                      }
                    }}
                    comboboxProps={selectComboboxProps}
                    styles={selectStyles}
                  />

                  <div>
                    <Text size="xs" mb={4}>
                      {t('modelViewer.presentation.skyboxHeight')}
                    </Text>
                    <Slider
                      min={0}
                      max={5}
                      step={0.1}
                      value={settings.skyboxHeight}
                      onChange={(value) => patch({ skyboxHeight: value })}
                      label={(value) => `${value.toFixed(1)}m`}
                      styles={sliderStyles}
                    />
                  </div>

                  <Select
                    label={t('modelViewer.presentation.backgroundLabel')}
                    data={backgroundOptions}
                    value={settings.backgroundColor}
                    onChange={(value) => {
                      if (value) {
                        patch({
                          backgroundColor: value as ModelViewerPresentationSettings['backgroundColor'],
                        });
                      }
                    }}
                    comboboxProps={selectComboboxProps}
                    styles={selectStyles}
                  />
                </>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="motion">
              {tabPanel(
                <>
                  <Switch
                    label={t('modelViewer.presentation.autoRotate')}
                    checked={settings.autoRotate}
                    onChange={(event) => patch({ autoRotate: event.currentTarget.checked })}
                    styles={switchStyles}
                  />

                  {settings.autoRotate ? (
                    <>
                      <div>
                        <Text size="xs" mb={4}>
                          {t('modelViewer.presentation.autoRotateDelay')}
                        </Text>
                        <Slider
                          min={0}
                          max={10}
                          step={0.5}
                          value={settings.autoRotateDelay / 1000}
                          onChange={(value) => patch({ autoRotateDelay: Math.round(value * 1000) })}
                          label={(value) => `${value.toFixed(1)}s`}
                          styles={sliderStyles}
                        />
                      </div>

                      <Select
                        label={t('modelViewer.presentation.rotationSpeedLabel')}
                        data={rotationOptions}
                        value={settings.rotationPerSecond}
                        onChange={(value) => {
                          if (value) {
                            patch({
                              rotationPerSecond:
                                value as ModelViewerPresentationSettings['rotationPerSecond'],
                            });
                          }
                        }}
                        comboboxProps={selectComboboxProps}
                        styles={selectStyles}
                      />
                    </>
                  ) : (
                    <Text size="xs" c="dimmed">
                      {t('modelViewer.presentation.autoRotateHint')}
                    </Text>
                  )}
                </>
              )}
            </Tabs.Panel>

            {hasMaterialsTab && (
              <Tabs.Panel value="materials">
                {tabPanel(
                  <Select
                    label={t('modelViewer.presentation.variantLabel')}
                    data={variantOptions}
                    value={settings.variantName || ''}
                    onChange={(value) => patch({ variantName: value ?? '' })}
                    comboboxProps={selectComboboxProps}
                    styles={selectStyles}
                  />
                )}
              </Tabs.Panel>
            )}
          </Tabs>

          <Button
            variant="light"
            size="xs"
            fullWidth
            styles={resetButtonStyles}
            onClick={() => onChange({ ...DEFAULT_MODEL_VIEWER_PRESENTATION })}
          >
            {t('modelViewer.presentation.reset')}
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
